import { useEffect, useMemo, type Ref } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { Calendar } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { webinarBookedOutAtom, webinarTimeslotAtom } from '@/atoms/demoAtoms';
import {
  MeetingBooker,
  type MeetingBookerHandle,
} from '@/container/demo-flow/meeting-booker';
import { useQueryWebinarTimeslots } from '@/hooks/query/useQueryWebinarTimeslots';
import { cn } from '@/lib/cn';
import { type DemoExperienceVariant } from '@/models/analytics/features/onboarding-hubspot';
import { type OnboardingExperience } from '@/utils/onboardingExperience';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Label } from '@heidi/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';

import { useCompletionStepAnalytics } from '@/models/analytics/features/useCompletionStepAnalytics';
import { formatWebinarSlotLabel, personalizationSharedMessages } from '@heidi/growth';
import { RequestDemoPhoneAndReasonFields } from './RequestDemoPhoneAndReasonFields';

interface DemoOptInSectionProps {
  experience: OnboardingExperience;
  webinarOptIn: boolean;
  onWebinarOptInChange: (value: boolean) => void;
  demoReason: string;
  onDemoReasonChange: (value: string) => void;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  defaultCountry?: Country;
  /** Country name used for the DEV-11979 phone-collection control events. */
  countryName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  meetingBookerRef?: Ref<MeetingBookerHandle>;
}

// Bridges the country-config-derived `OnboardingExperience` back to the legacy
// analytics enum so existing `OnboardingDemoVariantViewed` / opt-in events keep
// reporting the same variant strings.
const LEGACY_VARIANT: Record<OnboardingExperience['type'], DemoExperienceVariant> = {
  demo: 'meeting',
  'request-demo': 'sdr-demo',
  webinar: 'webinar',
  hidden: 'none',
};

/**
 * Variant-aware demo opt-in surface for the LearnBasics ClinicStep. Driven by
 * the country-config-derived `OnboardingExperience`:
 *
 * - `hidden`       → renders nothing
 * - `webinar`      → opt-in checkbox + region-aware timeslot picker
 * - `request-demo` → opt-in checkbox + free-text reason input
 * - `demo`         → standalone `MeetingBooker` (HubSpot calendar)
 *
 * Fires the `OnboardingDemoVariantViewed` impression on mount so variant
 * performance is comparable with V2's CompletionStep (which fires the same
 * event when its decision surface mounts).
 */
export function DemoOptInSection({
  experience,
  webinarOptIn,
  onWebinarOptInChange,
  demoReason,
  onDemoReasonChange,
  phoneNumber,
  onPhoneNumberChange,
  defaultCountry,
  countryName,
  firstName,
  lastName,
  email,
  companyName,
  meetingBookerRef,
}: DemoOptInSectionProps) {
  const intl = useIntl();
  const [webinarTimeslot, setWebinarTimeslot] = useAtom(webinarTimeslotAtom);
  const setWebinarBookedOut = useSetAtom(webinarBookedOutAtom);

  const demoExperienceVariant = LEGACY_VARIANT[experience.type];
  const webinarRegion = experience.type === 'webinar' ? experience.region : undefined;
  const tracking = useCompletionStepAnalytics({
    demoExperienceVariant,
    webinarRegion,
    country: countryName,
    countryCode: defaultCountry,
    language: intl.locale,
  });

  const {
    data: webinarTimeslots,
    isLoading: isLoadingTimeslots,
    isFetched: hasFetchedTimeslots,
  } = useQueryWebinarTimeslots(
    experience.type === 'webinar' && webinarOptIn ? webinarRegion : undefined
  );

  // Build localized display labels from the raw occurrence fields (the server
  // `label` is English-only). The English `slot.label` is preserved on the raw
  // `webinarTimeslots` for selection + analytics so tracked values stay stable
  // across UI languages.
  const webinarTimeslotItems = useMemo(
    () =>
      (webinarTimeslots ?? []).map((slot) => ({
        value: slot.value,
        label: formatWebinarSlotLabel(
          intl.locale,
          slot.startTime,
          slot.endTime,
          slot.timezone,
          slot.tzDisplay
        ),
      })),
    [webinarTimeslots, intl.locale]
  );

  // "Booked out" = opted into a webinar, the fetch has settled (success OR
  // error), and there are no sessions to pick. `isFetched` guards against a
  // false positive in the window between opt-in and the first fetch settling.
  const isWebinarBookedOut =
    experience.type === 'webinar' &&
    webinarOptIn &&
    hasFetchedTimeslots &&
    !isLoadingTimeslots &&
    webinarTimeslotItems.length === 0;

  // Surface the booked-out state so `useOnboardingCompletion` submits the
  // captured phone + reason via the SDR path instead of a webinar signup.
  useEffect(() => {
    setWebinarBookedOut(isWebinarBookedOut);
  }, [isWebinarBookedOut, setWebinarBookedOut]);

  if (experience.type === 'hidden') return null;

  if (experience.type === 'demo') {
    return (
      <div className="w-full max-w-[400px]">
        <MeetingBooker
          ref={meetingBookerRef}
          meetingUrl={experience.meetingUrl}
          firstName={firstName}
          lastName={lastName}
          email={email}
          companyName={companyName}
        />
      </div>
    );
  }

  const handleOptInChange = (checked: boolean) => {
    onWebinarOptInChange(checked);
    if (!checked) {
      setWebinarTimeslot(undefined);
      onDemoReasonChange('');
    }
    tracking.trackOptInChanged(checked);
  };

  const handleTimeslotSelect = (value: string | undefined) => {
    if (!value) {
      setWebinarTimeslot(undefined);
      return;
    }
    const selectedSlot = webinarTimeslots?.find((slot) => slot.value === value);
    if (!selectedSlot) return;
    setWebinarTimeslot({
      value: selectedSlot.value,
      label: selectedSlot.label,
      hubspotProperty: selectedSlot.hubspotProperty,
    });
    tracking.trackTimeslotSelected(selectedSlot.label, selectedSlot.hubspotProperty);
  };

  const showWebinarTimeslot = experience.type === 'webinar';
  const showDemoReason = experience.type === 'request-demo';

  return (
    // Intentionally renders without a card frame (no border, background, or
    // padding) so the webinar / request-demo opt-in surface sits flush on the
    // clinic-step page background — visually consistent with the `demo`
    // variant above (HubSpot `MeetingBooker`), which is also frameless. This
    // diverges from v2's `RequestDemoSection` (which uses
    // `bg-validation-info-muted rounded-xl border ...`) by design: the
    // LearnBasics flow's `ClinicStep` slots this directly into its own card
    // chrome, so a nested card frame would double up.
    <div className="flex w-full max-w-[400px] flex-col gap-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id="demo-webinar"
          checked={webinarOptIn}
          onCheckedChange={(checked) => handleOptInChange(checked === true)}
        />
        <Label htmlFor="demo-webinar" className="text-text-primary">
          <FormattedMessage
            {...(showWebinarTimeslot
              ? personalizationSharedMessages.bookDemoWebinarSignup
              : personalizationSharedMessages.requestDemoSignup)}
          />
        </Label>
      </div>

      {showWebinarTimeslot && (
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
            webinarOptIn ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            {isWebinarBookedOut ? (
              // Booked-out fallback: shown in place of the (empty) picker so
              // the user can still leave a phone number + reason for the SDR
              // team to follow up.
              <div className="flex w-full flex-col gap-3">
                <TypographyP2 className="text-pretty text-text-secondary">
                  <FormattedMessage
                    {...personalizationSharedMessages.webinarBookedOutFallback}
                  />
                </TypographyP2>
                <RequestDemoPhoneAndReasonFields
                  phoneNumber={phoneNumber}
                  onPhoneNumberChange={onPhoneNumberChange}
                  defaultCountry={defaultCountry}
                  demoReason={demoReason}
                  onDemoReasonChange={onDemoReasonChange}
                  disabled={!webinarOptIn}
                  country={countryName}
                  language={intl.locale}
                />
              </div>
            ) : (
              // Active webinar: timeslot picker plus optional phone + reason
              // demo-lead capture.
              <div className="flex w-full flex-col gap-3">
                <div className="flex w-full flex-col gap-1">
                  <TypographyP2Bold id="webinar-timeslot-label" className="leading-tight">
                    <FormattedMessage
                      {...personalizationSharedMessages.webinarTimeslotLabel}
                    />
                  </TypographyP2Bold>
                  <Select
                    value={webinarTimeslot?.value}
                    onValueChange={handleTimeslotSelect}
                    disabled={!webinarOptIn || isLoadingTimeslots}
                    aria-labelledby="webinar-timeslot-label"
                  >
                    <SelectTrigger>
                      <Calendar size={16} className="mr-1 text-text-secondary" />
                      <SelectValue
                        placeholder={intl.formatMessage(
                          isLoadingTimeslots
                            ? personalizationSharedMessages.webinarTimeslotLoading
                            : personalizationSharedMessages.webinarTimeslotPlaceholder
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {webinarTimeslotItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <RequestDemoPhoneAndReasonFields
                  optional
                  phoneNumber={phoneNumber}
                  onPhoneNumberChange={onPhoneNumberChange}
                  defaultCountry={defaultCountry}
                  demoReason={demoReason}
                  onDemoReasonChange={onDemoReasonChange}
                  disabled={!webinarOptIn}
                  country={countryName}
                  language={intl.locale}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {showDemoReason && (
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
            webinarOptIn ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
          aria-hidden={!webinarOptIn}
        >
          <div className="overflow-hidden">
            <RequestDemoPhoneAndReasonFields
              phoneNumber={phoneNumber}
              onPhoneNumberChange={onPhoneNumberChange}
              defaultCountry={defaultCountry}
              demoReason={demoReason}
              onDemoReasonChange={onDemoReasonChange}
              disabled={!webinarOptIn}
              country={countryName}
              language={intl.locale}
            />
          </div>
        </div>
      )}
    </div>
  );
}
